import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Table, Modal, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
// Import from Firebase services
import { getTreatments, addTreatment, updateTreatment, deleteTreatment } from '../../services/firebase/treatmentService';

const AdminTreatments = () => {
  const [treatments, setTreatments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentTreatment, setCurrentTreatment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    availability: '',
    availableDays: [],
    availableTimes: [],
    active: true
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [treatmentToDelete, setTreatmentToDelete] = useState(null);
  const navigate = useNavigate();

  // Available days and times for selection
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const timeSlots = [
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
    '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM'
  ];

  useEffect(() => {
    // Check if admin is logged in
    const isLoggedIn = localStorage.getItem('adminLoggedIn') === 'true';
    if (!isLoggedIn) {
      navigate('/admin/login');
      return;
    }
    
    loadTreatments();
  }, [navigate]);

  const loadTreatments = async () => {
    setLoading(true);
    try {
      const treatmentsData = await getTreatments();
      setTreatments(treatmentsData);
    } catch (error) {
      console.error('Error loading treatments:', error);
      setError('Failed to load treatments');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox' && name !== 'active') {
      // Handle day or time checkbox
      return;
    } else if (type === 'checkbox' && name === 'active') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleDayChange = (day, checked) => {
    if (day === 'all') {
      // Handle select all days
      if (checked) {
        setFormData({
          ...formData,
          availableDays: [...daysOfWeek] // Select all days
        });
      } else {
        setFormData({
          ...formData,
          availableDays: [] // Deselect all days
        });
      }
    } else {
      // Handle individual day selection
      if (checked) {
        setFormData({
          ...formData,
          availableDays: [...formData.availableDays, day]
        });
      } else {
        setFormData({
          ...formData,
          availableDays: formData.availableDays.filter(d => d !== day)
        });
      }
    }
  };

  const handleTimeChange = (time, checked) => {
    if (time === 'all') {
      // Handle select all times
      if (checked) {
        setFormData({
          ...formData,
          availableTimes: [...timeSlots] // Select all time slots
        });
      } else {
        setFormData({
          ...formData,
          availableTimes: [] // Deselect all time slots
        });
      }
    } else {
      // Handle individual time selection
      if (checked) {
        setFormData({
          ...formData,
          availableTimes: [...formData.availableTimes, time]
        });
      } else {
        setFormData({
          ...formData,
          availableTimes: formData.availableTimes.filter(t => t !== time)
        });
      }
    }
  };

  // Generate formatted text for available days
  const generateDaysText = (days) => {
    if (!days || days.length === 0) return 'Not available';
    
    let daysText = '';
    if (days.length === 1) {
      daysText = days[0];
    } else if (days.length === 2) {
      daysText = `${days[0]} and ${days[1]}`;
    } else if (days.length === 7) {
      daysText = 'Every day';
    } else if (days.includes('Monday') && days.includes('Tuesday') && 
               days.includes('Wednesday') && days.includes('Thursday') && 
               days.includes('Friday')) {
      if (days.length === 5) {
        daysText = 'Monday to Friday';
      } else if (days.includes('Saturday') && !days.includes('Sunday')) {
        daysText = 'Monday to Saturday';
      } else if (!days.includes('Saturday') && days.includes('Sunday')) {
        daysText = 'Sunday to Friday';
      }
    } else {
      daysText = days.join(', ');
      const lastComma = daysText.lastIndexOf(',');
      if (lastComma !== -1) {
        daysText = daysText.substring(0, lastComma) + ' and' + daysText.substring(lastComma + 1);
      }
    }
    
    return daysText;
  };
  
  // Generate formatted text for available times
  const generateTimesText = (times) => {
    if (!times || times.length === 0) return 'Not available';
    
    // Ensure all times are in a consistent format before processing
    const normalizedTimes = times.map(time => {
      // Handle case where time might not have a space or period
      if (!time.includes(' ')) {
        // Try to parse as 24-hour format and convert to 12-hour
        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours, 10);
        if (hour >= 0 && hour < 24) {
          const period = hour >= 12 ? 'PM' : 'AM';
          const hour12 = hour % 12 || 12; // Convert 0 to 12 for 12 AM
          return `${hour12}:${minutes} ${period}`;
        }
        return time; // Return as is if parsing fails
      }
      return time;
    });
    
    // Sort times using 12-hour format directly
    const sortedTimes = [...normalizedTimes].sort((a, b) => {
      // Extract hours, minutes, and period for comparison
      const [timeA, periodA] = a.split(' ');
      const [timeB, periodB] = b.split(' ');
      
      if (!timeA || !periodA || !timeB || !periodB) {
        console.error('Invalid time format:', a, b);
        return 0;
      }
      
      const [hoursA, minutesA] = timeA.split(':');
      const [hoursB, minutesB] = timeB.split(':');
      
      // Compare periods first (AM comes before PM)
      if (periodA !== periodB) {
        return periodA === 'AM' ? -1 : 1;
      }
      
      // For same period, compare hours
      const hourA = parseInt(hoursA, 10);
      const hourB = parseInt(hoursB, 10);
      
      // Special handling for 12 o'clock
      const normalizedHourA = hourA === 12 ? 0 : hourA;
      const normalizedHourB = hourB === 12 ? 0 : hourB;
      
      if (normalizedHourA !== normalizedHourB) {
        return normalizedHourA - normalizedHourB;
      }
      
      // If hours are equal, compare minutes
      return parseInt(minutesA, 10) - parseInt(minutesB, 10);
    });
    
    // Format times to ensure they use 12-hour format with leading zeros removed
    const formattedTimes = sortedTimes.map(time => {
      if (!time.includes(' ')) return time; // Skip invalid formats
      
      const [timeStr, period] = time.split(' ');
      if (!timeStr || !period) return time; // Skip invalid formats
      
      const [hours, minutes] = timeStr.split(':');
      if (!hours || !minutes) return time; // Skip invalid formats
      
      // Remove leading zero from hours (e.g., '09:30 AM' becomes '9:30 AM')
      const formattedHour = hours.startsWith('0') ? hours.substring(1) : hours;
      return `${formattedHour}:${minutes} ${period}`;
    });
    
    // If there are many time slots, just show the range
    if (formattedTimes.length > 3) {
      const firstTime = formattedTimes[0];
      const lastTime = formattedTimes[formattedTimes.length - 1];
      return `${firstTime} to ${lastTime}`;
    } else {
      return formattedTimes.join(', ');
    }
  };
  
  // For backward compatibility, keep the original function
  const generateAvailabilityText = (days, times) => {
    const daysText = generateDaysText(days);
    const timesText = generateTimesText(times);
    
    if (daysText === 'Not available' || timesText === 'Not available') {
      return daysText;
    }
    
    return `${daysText}, ${timesText}`;
  };

  const handleAddTreatment = () => {
    setIsEditing(false);
    setCurrentTreatment(null);
    setFormData({
      name: '',
      description: '',
      availability: '',
      availableDays: [],
      availableTimes: [],
      active: true
    });
    setError('');
    setShowModal(true);
  };

  const handleEditTreatment = (treatment) => {
    setIsEditing(true);
    setCurrentTreatment(treatment);
    setFormData({
      name: treatment.name || '',
      description: treatment.description || '',
      availability: treatment.availability || '',
      availableDays: treatment.availableDays || [],
      availableTimes: treatment.availableTimes || [],
      active: treatment.active !== undefined ? treatment.active : true
    });
    setError('');
    setShowModal(true);
  };

  const handleDeleteTreatment = (treatment) => {
    setTreatmentToDelete(treatment);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!treatmentToDelete || deleteInProgress) return;
    
    setDeleteInProgress(true);
    setShowDeleteConfirm(false);
    setError('');
    
    try {
      console.log('Deleting treatment:', treatmentToDelete.id, treatmentToDelete.name);
      await deleteTreatment(treatmentToDelete.id);
      console.log('Treatment deleted successfully');
      await loadTreatments();
      setSuccess('Treatment deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Delete failed:', err);
      setError('Failed to delete treatment: ' + (err.message || 'Unknown error'));
      setTimeout(() => setError(''), 5000);
    } finally {
      setDeleteInProgress(false);
      setTreatmentToDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate required fields
      if (!formData.name || !formData.description) {
        setError('Name and description are required');
        return;
      }

      if (formData.availableDays.length === 0 || formData.availableTimes.length === 0) {
        setError('Please select at least one day and time');
        return;
      }
      
      // Generate availability text from selected days and times
      const availabilityText = generateAvailabilityText(formData.availableDays, formData.availableTimes);
      
      const treatmentData = {
        ...formData,
        availability: availabilityText
      };
      
      if (isEditing && currentTreatment) {
        await updateTreatment(currentTreatment.id, treatmentData);
        setSuccess('Treatment updated successfully');
      } else {
        await addTreatment(treatmentData);
        setSuccess('Treatment added successfully');
      }
      
      await loadTreatments();
      setShowModal(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    navigate('/admin/login');
  };

  return (
    <section className="page-section">
      <Container fluid>
        <Row className="mb-4 align-items-center">
          <Col>
            <h2>Manage Treatments</h2>
            <p>Add, edit, or remove treatment options and their availability</p>
          </Col>
          <Col xs="auto">
            <Button variant="outline-danger" onClick={handleLogout}>Logout</Button>
          </Col>
        </Row>
        
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Treatment Options</h4>
            <Button variant="primary" size="sm" onClick={handleAddTreatment}>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Add Treatment
            </Button>
          </Card.Header>
          <Card.Body>
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>Treatment Name</th>
                  <th>Description</th>
                  <th>Availability Date</th>
                  <th>Availability Time</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center">Loading...</td>
                  </tr>
                ) : treatments.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center">No treatments found</td>
                  </tr>
                ) : (
                  treatments.map(treatment => (
                    <tr key={treatment.id}>
                      <td>{treatment.name}</td>
                      <td>{treatment.description}</td>
                      <td>{generateDaysText(treatment.availableDays)}</td>
                      <td>{generateTimesText(treatment.availableTimes)}</td>
                      <td>
                        <span className={`badge bg-${treatment.active ? 'success' : 'secondary'}`}>
                          {treatment.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex">
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="me-2"
                            onClick={() => handleEditTreatment(treatment)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDeleteTreatment(treatment)}
                            disabled={deleteInProgress}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
        
        {/* Treatment Form Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isEditing ? 'Edit Treatment' : 'Add New Treatment'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Row className="mb-3">
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Treatment Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <div>
                      <Form.Check
                        type="checkbox"
                        id="treatment-active"
                        label="Active"
                        name="active"
                        checked={formData.active}
                        onChange={handleChange}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Available Days</Form.Label>
                    <div className="mb-2">
                      <Form.Check
                        type="checkbox"
                        id="day-all"
                        label="Select All"
                        checked={formData.availableDays.length === daysOfWeek.length}
                        onChange={(e) => handleDayChange('all', e.target.checked)}
                        className="fw-bold"
                      />
                    </div>
                    <hr className="my-2" />
                    {daysOfWeek.map((day) => (
                      <div key={day}>
                        <Form.Check
                          type="checkbox"
                          id={`day-${day}`}
                          label={day}
                          checked={formData.availableDays.includes(day)}
                          onChange={(e) => handleDayChange(day, e.target.checked)}
                        />
                      </div>
                    ))}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Available Times</Form.Label>
                    <div className="mb-2">
                      <Form.Check
                        type="checkbox"
                        id="time-all"
                        label="Select All"
                        checked={formData.availableTimes.length === timeSlots.length}
                        onChange={(e) => handleTimeChange('all', e.target.checked)}
                        className="fw-bold"
                      />
                    </div>
                    <hr className="my-2" />
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {timeSlots.map((time) => (
                        <div key={time}>
                          <Form.Check
                            type="checkbox"
                            id={`time-${time}`}
                            label={time}
                            checked={formData.availableTimes.includes(time)}
                            onChange={(e) => handleTimeChange(time, e.target.checked)}
                          />
                        </div>
                      ))}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              
              <div className="d-flex justify-content-end">
                <Button variant="secondary" className="me-2" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  type="submit"
                  disabled={formData.availableDays.length === 0 || formData.availableTimes.length === 0}
                >
                  {isEditing ? 'Update' : 'Add'} Treatment
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteConfirm} onHide={() => { setShowDeleteConfirm(false); setTreatmentToDelete(null); }} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete <strong>{treatmentToDelete?.name}</strong>? This action cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowDeleteConfirm(false); setTreatmentToDelete(null); }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete} disabled={deleteInProgress}>
              {deleteInProgress ? 'Deleting...' : 'Delete'}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </section>
  );
};

export default AdminTreatments;